/** You › Security: passkeys, trusted devices, app lock, recovery keys. */
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@viko/transcript-ui'
import {
  CircleAlert,
  Clock,
  Fingerprint,
  KeyRound,
  ShieldCheck,
  Smartphone,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { LockSettings } from '../../components/LockSettings.js'
import { LostDeviceSection } from '../../components/LostDevice.js'
import { RecoveryKeys } from '../../components/RecoveryKeys.js'
import { Ago, errorText, useToast } from '../../components/ui.js'
import { useBackend } from '../../lib/backend.js'
import { endorseDevice, endorsementState, loadDevice } from '../../lib/device-key.js'
import { removeDevice } from '../../lib/device-removal.js'
import { passkeyErrorMessage, passkeys } from '../../lib/passkeys.js'
import { qk, useCredentials, useDevices } from '../../lib/queries.js'
import type { Device } from '../../lib/types.js'
import { Section } from './shared.js'

export function SecuritySection() {
  return (
    <>
      <Passkeys />
      <Devices />
      {/* ---- faceid-recovery: lost device revokes its synced passkey too ---- */}
      <Section id="lost-device" title="Lost device">
        <LostDeviceSection />
      </Section>
      {/* ---- end faceid-recovery ---- */}
      {/* ---- lock-follows-os: app lock policy ---- */}
      <Section id="security" title="App lock">
        <LockSettings />
      </Section>
      {/* ---- end lock-follows-os ---- */}
      <Section id="recovery-keys" title="Recovery & keys">
        <RecoveryKeys />
      </Section>
    </>
  )
}

function Passkeys() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const creds = useCredentials()
  const add = async () => {
    try {
      const options = await b.api.credentialOptions('viko')
      const resp = await passkeys.register(options)
      await b.api.credentialVerify(resp, 'New passkey')
      await qc.invalidateQueries({ queryKey: qk.credentials })
      toast('Passkey added. Approve it on each machine when prompted.')
    } catch (e) {
      toast(passkeyErrorMessage(e), 'error')
    }
  }
  const list = creds.data ?? []
  return (
    <Section
      id="passkeys"
      title="Passkeys"
      action={
        <Button size="sm" icon={<KeyRound size={14} />} onClick={() => void add()}>
          Add passkey
        </Button>
      }
    >
      {list.length < 2 && !list.some((c) => c.backed_up) && creds.isSuccess ? (
        <p className="st-note is-wait">
          <CircleAlert size={16} aria-hidden="true" />
          <span>
            Your passkey lives on one device. Add a second one so losing it never locks you out.
          </span>
        </p>
      ) : null}
      <ul className="st-rows">
        {list.map((c) => (
          <li key={c.id} className="st-row st-row--2">
            <span className="st-row__icon">
              <ShieldCheck size={16} aria-hidden="true" />
            </span>
            <span className="st-row__main">
              <span className="st-row__title">{c.name ?? 'Passkey'}</span>
              <span className="st-row__meta">
                {c.backed_up ? 'Synced' : 'Hardware'} · added{' '}
                {new Date(c.created_at).toLocaleDateString()}
                {c.last_used_at ? (
                  <>
                    {' '}
                    · used <Ago ts={c.last_used_at} />
                  </>
                ) : null}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/** "Revoked", "Temporary · until 18:40 (this session)", "Trusted until 12/28/2026". */
export function deviceMeta(
  d: Pick<Device, 'revoked_at' | 'expires_at' | 'session'>,
  now = Date.now(),
): string {
  if (d.revoked_at) return 'Revoked'
  if (d.session)
    return d.expires_at <= now
      ? 'Temporary · session ended'
      : `Temporary · this session, until ${new Date(d.expires_at).toLocaleString([], {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}`
  return `Trusted until ${new Date(d.expires_at).toLocaleDateString()}`
}

function Devices() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const devices = useDevices()
  const [mine, setMine] = useState<string | undefined>()
  const [state, setState] = useState<ReturnType<typeof endorsementState>>('ok')
  useEffect(() => {
    void loadDevice().then((d) => {
      setMine(d?.deviceKeyId)
      setState(endorsementState(d))
    })
  }, [])
  const retrust = async () => {
    try {
      await endorseDevice(passkeys, (e) => b.api.registerDevice(e))
      setState('ok')
      await qc.invalidateQueries({ queryKey: qk.devices })
      toast('This device is trusted for 90 days')
    } catch (e) {
      toast(passkeyErrorMessage(e), 'error')
    }
  }
  return (
    <Section id="devices" title="Devices">
      {state === 'session' ? (
        <div className="st-note is-wait">
          <Clock size={16} aria-hidden="true" />
          <span className="flex-1">
            This browser is signed in for this session only. Its key lives in this tab and ends when
            you close it or sign out. To trust it, sign out and choose Trust this device.
          </span>
        </div>
      ) : null}
      {state === 'renew' || state === 'unendorsed' ? (
        <div className="st-note is-wait items-center">
          <CircleAlert size={16} aria-hidden="true" />
          <span className="flex-1">
            {state === 'renew'
              ? 'This device’s trust expires within 14 days.'
              : 'This device is not trusted.'}
          </span>
          <Button
            size="sm"
            variant="primary"
            icon={<Fingerprint size={14} />}
            onClick={() => void retrust()}
          >
            Renew
          </Button>
        </div>
      ) : null}
      <ul className="st-rows">
        {(devices.data ?? []).map((d) => (
          <li key={d.id} className="st-row">
            <span className="st-row__icon">
              <Smartphone size={16} aria-hidden="true" />
            </span>
            <span className="st-row__main">
              <span className="st-row__title">
                {d.label}
                {d.id === mine ? (
                  <span className="ml-2 text-[14px] font-semibold text-accent-text">
                    This device
                  </span>
                ) : null}
              </span>
              <span className="st-row__meta">
                {deviceMeta(d)}
                {d.unlockedVia ? (
                  <>
                    {' '}
                    · Unlocked via {d.unlockedVia.machineName} <Ago ts={d.unlockedVia.at} />
                  </>
                ) : null}
              </span>
            </span>
            {!d.revoked_at && d.id !== mine ? (
              <Button
                size="sm"
                variant="ghost"
                icon={<Trash2 size={14} />}
                aria-label={`Revoke ${d.label}`}
                onClick={async () => {
                  if (!confirm(`Revoke ${d.label}? Commands from it will be refused.`)) return
                  try {
                    // A session browser: ended at the hub and daemons, no key rotation. A trusted
                    // one: signed revocation and a rotation to keys it can no longer read.
                    await removeDevice(d, { content: b.content, api: b.api })
                    await qc.invalidateQueries({ queryKey: qk.devices })
                    toast(`${d.label} revoked`)
                  } catch (e) {
                    toast(errorText(e), 'error')
                  }
                }}
              >
                Revoke
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Section>
  )
}
